import DataTable from '@/components/ui/DataTable';
import {
  getCoreRowModel,
  useReactTable,
  type RowSelectionState,
} from '@tanstack/react-table';
import type { Updater } from '@tanstack/table-core';
import {
  CIRCUIT_SELECTION_COLUMNS,
  PublishedCircuit,
} from '@/types/distributors/CircuitSelectionColumnDef';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';

interface CircuitSelectionTableProps {
  filteredCircuits: PublishedCircuit[];
  agencies: string[];
  setSelectedCircuitRows: React.Dispatch<
    React.SetStateAction<RowSelectionState>
  >;
  shouldSelectAll: boolean;
}

const CircuitSelectionTable = ({
  filteredCircuits,
  agencies,
  setSelectedCircuitRows,
  shouldSelectAll = false,
}: CircuitSelectionTableProps) => {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const defaultRowSelection = useMemo(
    () =>
      filteredCircuits
        .filter((circuit) => circuit._count.sharedCircuits === agencies.length)
        .map((circuit) => circuit.id)
        .reduce((acc, circuitId) => ({ ...acc, [circuitId]: true }), {}),
    [agencies.length, filteredCircuits],
  );

  const [rowSelection, setRowSelection] =
    useState<RowSelectionState>(defaultRowSelection);

  const prevShouldSelectAllRef = useRef<boolean | undefined>(undefined);
  const isFirstSyncRef = useRef(true);

  const circuits = useMemo(
    () =>
      filteredCircuits.map((circuit) => ({
        ...circuit,
        agenciesCount: agencies.length,
      })),
    [agencies.length, filteredCircuits],
  );

  const columns = useMemo(
    () =>
      getTranslatedConst(CIRCUIT_SELECTION_COLUMNS).slice(
        0,
        agencies.length === 1 ? undefined : -2,
      ),
    [agencies.length, getTranslatedConst],
  );

  const onRowSelectionChange = useCallback(
    (updater: Updater<RowSelectionState>) => {
      setRowSelection((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        setSelectedCircuitRows(next);
        return next;
      });
    },
    [setSelectedCircuitRows],
  );

  const table = useReactTable({
    data: circuits,
    columns,
    state: {
      rowSelection,
    },
    onRowSelectionChange,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  useEffect(() => {
    const prevWasSelectAll = prevShouldSelectAllRef.current;

    if (shouldSelectAll) {
      table.toggleAllRowsSelected(true);
    } else if (prevWasSelectAll === true) {
      table.setRowSelection(defaultRowSelection);
    } else if (isFirstSyncRef.current) {
      table.setRowSelection(defaultRowSelection);
    }

    isFirstSyncRef.current = false;
    prevShouldSelectAllRef.current = shouldSelectAll;
  }, [shouldSelectAll, filteredCircuits, defaultRowSelection, table]);

  return (
    <div className="flex flex-col gap-2">
      <p className="text-base font-medium text-black">
        {t('distributors.modals.circuitSelection.yourPublishedCircuits')} (
        {filteredCircuits.length})
      </p>
      <div className="h-[492px] overflow-y-auto">
        <DataTable
          table={table}
          onRowClick={(row) => row.toggleSelected(!row.getIsSelected())}
        />
      </div>
    </div>
  );
};

export default CircuitSelectionTable;
